/**
 * Product registry.
 *
 * Adding a product to the site is adding one object to PRODUCTS — the
 * homepage section, the /products page, the footer column and the nav
 * all read from here. No new components, no layout surgery.
 *
 * `status` drives the badge and controls whether we make customer claims:
 *   live      — in production with paying customers. May claim customers.
 *   pilot     — running with real users, not yet a paying deployment.
 *   building  — in the lab. No customer claim of any kind.
 */

export type ProductStatus = "live" | "pilot" | "building";

export interface Product {
  slug: string;
  name: string;
  /** One line, under six words. Sits directly under the name. */
  tagline: string;
  /** The sentence a business reader needs. Two lines maximum. */
  summary: string;
  status: ProductStatus;
  /** Short, factual presence line. Rendered next to the status dot. */
  presence: string;
  sector: string;
  capabilities: string[];
  /** Where "Explore" goes. Internal path or absolute URL. */
  href: string;
  /** Sign-in / live app, when there is one. */
  appHref?: string;
  /**
   * Public app-store listings. Only fill these in once the listing is
   * actually live and reachable — a store link that 404s reads worse than
   * no store link at all.
   */
  stores?: { ios?: string; android?: string };
  /** Real product screenshots, served from public/. Absolute paths only —
   *  relative paths break at /hazribook without a trailing slash. */
  shots?: { src: string; alt: string; caption: string }[];
  accent: "emerald" | "violet";
}

export const PRODUCTS: Product[] = [
  {
    slug: "agent-labista",
    name: "Agent Labista",
    tagline: "AI sales agent for real estate",
    summary:
      "Every WhatsApp enquiry answered the minute it arrives, in the buyer's own language, from the builder's own brochures — then qualified, scored and booked into a site visit.",
    // Not "live". The product works and can be demonstrated, but no builder
    // is using it: there are no customers and the backend is not deployed.
    // `live` is the only status that permits a customer claim, and claiming
    // one here would be the exact failure this registry exists to prevent.
    status: "building",
    presence: "In development — not yet open to customers",
    sector: "Real estate",
    capabilities: [
      "WhatsApp Business API, live on the builder's own number",
      "Eight languages, matched to the buyer's script and register",
      "Answers grounded in the builder's documents, with citations",
      "Progressive lead capture — name, area, budget, timeline",
      "Site visits booked into the sales team's calendar",
      "Source attribution preserved across direct, CP and paid channels",
    ],
    href: "/labista",
    // Deliberately NO appHref. app.astalabista.com still serves the admin
    // shell, but its API (agent-labista-api / api.astalabista.com) has been
    // decommissioned — the Fly app is gone and the DNS record does not
    // exist, so nothing there can authenticate. Offering a "Sign in" link
    // to a console that cannot sign anyone in is exactly the kind of claim
    // this site does not make. Restore it here when the API is back.
    accent: "violet",
  },
  {
    slug: "hazri-book",
    name: "Hazri Book",
    tagline: "Verified attendance for field workforces",
    summary:
      "Replaces the paper hazri register. Every shift carries a photo, a GPS reading and a server-set timestamp — and anything missing is flagged on the record rather than quietly dropped.",
    status: "live",
    presence: "In production with a Saudi contracting company",
    sector: "Construction and contracting",
    capabilities: [
      "Photo, location and server-set time captured at every punch",
      "Gaps flagged as exceptions, never silently discarded",
      "Works offline, syncs when the site gets signal",
      "Arabic and English, right-to-left throughout",
      "Overtime authorised before it is payable",
      "Month-end lock and payroll-ready exports",
    ],
    href: "/hazribook",
    appHref: "https://hazribook.astalabista.com",
    // Verified live 2026-09-02: Play Store returns 200, and Apple's lookup
    // API reports "Hazri Book" 0.5.0 published across 175 territories,
    // Saudi Arabia and the UAE included.
    stores: {
      ios: "https://apps.apple.com/app/id6797261924",
      android:
        "https://play.google.com/store/apps/details?id=com.hazribook.worker",
    },
    shots: [
      {
        src: "/hazribook/shots/dashboard.png",
        alt: "Hazri Book company dashboard showing labour cost, live presence across three sites and exception flags",
        caption: "Company dashboard — cost, live presence, exceptions",
      },
      {
        src: "/hazribook/shots/register.png",
        alt: "Hazri Book attendance register",
        caption: "Attendance register",
      },
      {
        src: "/hazribook/shots/compliance.png",
        alt: "Hazri Book compliance view tracking worker credential expiry",
        caption: "Credential compliance",
      },
      {
        src: "/hazribook/shots/timesheets.png",
        alt: "Hazri Book timesheets ready for payroll export",
        caption: "Payroll-ready timesheets",
      },
    ],
    accent: "emerald",
  },
];

export const STATUS_LABEL: Record<ProductStatus, string> = {
  live: "Live",
  pilot: "In pilot",
  building: "In the lab",
};

export function productBySlug(slug: string): Product | undefined {
  return PRODUCTS.find((p) => p.slug === slug);
}
